import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { personService } from "@/features/pessoas/services/personService";

interface PessoaSearchComboboxProps {
  value?: number;
  onChange: (value: number) => void;
  filter?: "cliente" | "fornecedor";
  placeholder?: string;
  disabled?: boolean;
}

export const PessoaSearchCombobox = ({
  value,
  onChange,
  filter,
  placeholder = "Selecione...",
  disabled,
}: PessoaSearchComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["pessoas-search", search, filter],
    queryFn: async () => {
      if (!search.trim()) return [];
      const all = await personService.search(search);
      if (filter === "cliente") return all.filter((p) => p.isCliente);
      if (filter === "fornecedor") return all.filter((p) => p.isFornecedor);
      return all;
    },
    enabled: open,
    staleTime: 10_000,
  });

  const { data: selectedPerson } = useQuery({
    queryKey: ["pessoa", value],
    queryFn: () => personService.getById(value!),
    enabled: !!value,
    staleTime: 60_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          type="button"
        >
          <span className="truncate">
            {selectedPerson ? selectedPerson.nomeRazao : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar pelo nome ou CPF/CNPJ..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && (
              <CommandEmpty>
                {search.trim()
                  ? "Nenhum resultado encontrado."
                  : "Digite para buscar..."}
              </CommandEmpty>
            )}
            <CommandGroup>
              {results.map((pessoa) => (
                <CommandItem
                  key={pessoa.id}
                  value={String(pessoa.id)}
                  onSelect={() => {
                    onChange(pessoa.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === pessoa.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{pessoa.nomeRazao}</span>
                    {pessoa.cpfCnpj && (
                      <span className="text-xs text-muted-foreground">
                        {pessoa.cpfCnpj}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
